import React, { useLayoutEffect, useRef, useState } from 'react';

interface IUseClampedTextResult {
  ref: React.RefObject<HTMLElement>;
  isOverflowing: boolean;
  expanded: boolean;
  toggleExpanded: () => void;
}

// Clamps a block of text to a fixed number of lines and reports whether it is actually
// overflowing (measured via `scrollHeight > clientHeight + 1`) so callers can
// conditionally render a "Show more"/"Show less" toggle. Measurement only reads the collapsed
// state, so when the text changes, it first collapses any expansion to avoid a stale "Show
// less" on now-shorter content. `enabled` gates the clamp entirely (pass `false` to never
// clamp); `text` re-triggers measurement, as does any later resize of the element itself —
// content can carry images that only grow the box once they finish loading.
export const useClampedText = (enabled: boolean, text: string): IUseClampedTextResult => {
  const ref = useRef<HTMLElement>(null);
  const prevTextRef = useRef(text);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) {
      setIsOverflowing(false);

      return;
    }

    if (prevTextRef.current !== text) {
      prevTextRef.current = text;
      if (expanded) {
        setExpanded(false);

        return;
      }
    }

    const el = ref.current;
    if (!el) {
      return;
    }

    if (expanded) {
      setIsOverflowing(true);

      return;
    }

    const measure = () => setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    // Observes the clamped box, whose own height is pinned by the clamp; late-loading images
    // change scrollHeight without resizing it, so watch the children that can actually grow.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    Array.from(el.querySelectorAll('img')).forEach((img) => observer.observe(img));

    return () => observer.disconnect();
  }, [enabled, expanded, text]);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return { ref, isOverflowing, expanded, toggleExpanded };
};
